import { CategoryService } from './services/category.service';
import { JobService } from './services/job.service';
import { SubscriptionService } from './services/subscription.service';
import React, { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';

import { Category } from './models/category.model';
import { JobContent, JobResponse } from './models';
import JobCard from './components/card';
import FilterPanel, { ActiveFilter } from './components/FilterPanel';
import McpAvailabilityTeaser from '../components/McpAvailabilityTeaser';
import Swal from 'sweetalert2';
import './job.css';
import ReactGA from 'react-ga4';
import { Modal } from 'antd';
import {
  UserAddOutlined,
  InboxOutlined,
  ReloadOutlined,
  SearchOutlined,
} from '@ant-design/icons';

// Wait ~1s after the user stops typing before searching (debounce resets on each keystroke).
const SEARCH_DEBOUNCE_MS = 1000;

function formatCategorySummary(categories: string[]): string {
  if (categories.length === 0) {
    return '';
  }
  if (categories.length === 1) {
    return categories[0];
  }
  if (categories.length === 2) {
    return `${categories[0]} and ${categories[1]}`;
  }
  return `${categories.length} categories`;
}

const Jobs: React.FC = () => {
  const [page, setPage] = useState<number>(1);
  const [spinner, setSpinner] = useState<boolean>(true);
  const [isFiltering, setIsFiltering] = useState<boolean>(false);
  const [blockFilter, setBlockFilter] = useState<boolean>(false);
  const [filter, setFilter] = useState<JobContent>(new JobContent());
  const [searchTerm, setSearchTerm] = useState<string>('');
  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const filterRef = useRef(filter);

  const [jobs, setJobs] = useState<JobResponse>(new JobResponse());
  const [categories, setCategories] = useState<Category[]>([]);
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const selectedCategoriesRef = useRef<string[]>([]);

  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [modalBody, setModalBody] = useState<React.ReactNode>(null);

  const [subscriptionType, setSubscriptionType] = useState<string | null>('');

  useEffect(() => {
    filterRef.current = filter;
  }, [filter]);

  useEffect(() => {
    selectedCategoriesRef.current = selectedCategories;
  }, [selectedCategories]);

  const [resultsEnter, setResultsEnter] = useState(false);
  const wasFilteringRef = useRef(false);

  useEffect(() => {
    if (wasFilteringRef.current && !isFiltering) {
      setResultsEnter(true);
      const timeoutId = window.setTimeout(() => setResultsEnter(false), 450);
      wasFilteringRef.current = isFiltering;
      return () => window.clearTimeout(timeoutId);
    }

    wasFilteringRef.current = isFiltering;
  }, [isFiltering]);

  useEffect(() => {
    const Initialize = async () => {
      try {
        const categories = await CategoryService.GetCategories();
        setCategories(categories);

        const categoryParams = searchParams.getAll('category').filter(Boolean);
        const queryParam = searchParams.get('q') ?? '';
        const trimmedQuery = queryParam.trim();

        setSearchTerm(queryParam);
        setSelectedCategories(categoryParams);

        const newFilter: JobContent = {
          ...filter,
          category: '',
          subCategory: '',
        };
        setFilter(newFilter);
        filterRef.current = newFilter;
        selectedCategoriesRef.current = categoryParams;

        if (categories.length !== 0) {
          const result = await GetJobs(categoryParams, '', page, trimmedQuery);

          const isEmpty =
            !result.content ||
            result.content.length === 0 ||
            result.totalElements === 0;

          setJobs(isEmpty ? new JobResponse() : result);
        }
      } catch (error: unknown) {
        showFilterToast(error);
      }

      setSpinner(false);
    };

    Initialize();
  }, []);

  useEffect(() => {
    return () => {
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const checkSubscription = async () => {
      const subscriptionAction = searchParams.get('subscription');
      const subscriptionHash = searchParams.get('hash');
      const message = searchParams.get('message');

      setSubscriptionType(subscriptionAction);

      if (!subscriptionAction) return;

      if (subscriptionAction !== 'error') {
        const isValidHash = await hashCodeConfirmation(subscriptionHash);
        if (!isValidHash) return;
      }

      switch (subscriptionAction) {
        case 'on':
          setModalBody(
            <>
              <h1 className="jo-modal-info-title mb-4 text-3xl font-bold">
                Subscription Activated!
              </h1>
              <p className="jo-modal-body-text mb-2 text-base">
                Your subscription is now active. You'll start receiving{' '}
                <strong>daily job posts</strong> tailored to your selected
                categories.
              </p>
              <p className="jo-modal-body-muted text-base">
                Stay tuned — your next opportunity might be just an email away!
              </p>
            </>
          );
          break;

        case 'error':
          setModalBody(
            <>
              <h1 className="jo-modal-error-title mb-4 text-3xl font-bold">
                Subscription Failed!
              </h1>
              <p className="jo-modal-body-text mb-2 text-base">
                Something went wrong while activating your subscription. Please
                try again or contact support if the problem persists.
              </p>
              <p className="jo-modal-body-text mb-2 text-base">
                <strong>Error:</strong>{' '}
                <span className="font-mono">{message}</span>
              </p>
              <p className="jo-modal-body-muted text-base">
                Need help?{' '}
                <a
                  href={`mailto:${process.env.REACT_APP_CONTACT_URL ?? ''}`}
                  className="jo-modal-error-link"
                >
                  Contact our support team.
                </a>
              </p>
            </>
          );
          break;

        case 'removed':
          setModalBody(
            <>
              <h1 className="jo-modal-warning-title mb-4 text-3xl font-bold">
                Unsubscribed Successfully
              </h1>
              <p className="jo-modal-body-text mb-2 text-base">
                You have been successfully unsubscribed. You will no longer
                receive daily job posts.
              </p>
              <p className="jo-modal-body-muted text-base">
                Change your mind?{' '}
                <Link
                  to="/subscribe"
                  className="jo-modal-warning-link"
                  onClick={() => {
                    setModalOpen(false);
                    searchParams.delete('subscription');
                    searchParams.delete('message');
                    searchParams.delete('hash');
                  }}
                >
                  Subscribe again here.
                </Link>
              </p>
            </>
          );
          break;

        default:
          return;
      }

      setModalOpen(true);
    };

    checkSubscription();
  }, [searchParams]);

  const GetJobs = async (
    categoryList: string[],
    subcategory: string,
    page: number,
    query: string = searchTerm
  ) => {
    return await JobService.GetJobs(categoryList, subcategory, page, query);
  };

  const resetToInitialState = async () => {
    setSearchTerm('');
    setSelectedCategories([]);
    selectedCategoriesRef.current = [];
    setPage(1);

    const cleared: JobContent = {
      ...filter,
      category: '',
      subCategory: '',
    };
    setFilter(cleared);
    filterRef.current = cleared;
    updateFilterUrl([], '');

    await fetchFilteredJobs([], '');
  };

  const ClearFilter = async () => {
    if (blockFilter || isFiltering) {
      return;
    }

    ReactGA.send({
      hitType: 'pageview',
      page: '/jobs',
      title: 'Jobs Home : Cleared',
    });

    await resetToInitialState();
  };

  const showFilterToast = (error: unknown) => {
    if (error instanceof Error && error.message.includes('SHOW: ')) {
      Swal.fire({
        toast: true,
        position: 'top-end',
        icon: 'info',
        title: error.message.slice(5, error.message.length),
        showConfirmButton: false,
        timer: 3000,
        timerProgressBar: true,
        didOpen: (toast) => {
          toast.addEventListener('mouseenter', Swal.stopTimer);
          toast.addEventListener('mouseleave', Swal.resumeTimer);
        },
      });
    }
  };

  const updateFilterUrl = (categoryList: string[], query: string) => {
    const newSearchParams = new URLSearchParams(searchParams);

    newSearchParams.delete('category');
    categoryList.forEach((category) => {
      newSearchParams.append('category', category);
    });

    newSearchParams.delete('subcategory');

    if (query) {
      newSearchParams.set('q', query);
    } else {
      newSearchParams.delete('q');
    }

    const newUrl = newSearchParams.toString()
      ? `/jobs?${newSearchParams.toString()}`
      : '/jobs';
    navigate(newUrl, { replace: true });
  };

  const fetchFilteredJobs = async (categoryList: string[], query: string) => {
    const trimmedQuery = query.trim();
    setIsFiltering(true);
    setPage(1);

    try {
      const result = await GetJobs(categoryList, '', 1, trimmedQuery);
      const isEmpty =
        !result.content ||
        result.content.length === 0 ||
        result.totalElements === 0;

      setJobs(isEmpty ? new JobResponse() : result);
    } catch (error: unknown) {
      if (error instanceof Error && error.message.includes('SHOW:')) {
        setJobs(new JobResponse());
      }
      showFilterToast(error);
    }

    setIsFiltering(false);
  };

  const HandleCategorySelect = async (categoryNames: string[]) => {
    if (blockFilter) {
      return;
    }

    const nextCategories = categoryNames.filter(Boolean);
    setSelectedCategories(nextCategories);
    selectedCategoriesRef.current = nextCategories;

    const newFilter: JobContent = {
      ...filter,
      category: '',
      subCategory: '',
    };

    setFilter(newFilter);
    filterRef.current = newFilter;
    updateFilterUrl(nextCategories, searchTerm.trim());

    const categorySummary = formatCategorySummary(nextCategories);
    ReactGA.send({
      hitType: 'pageview',
      page: nextCategories.length
        ? `/jobs?category=${nextCategories.join('&category=')}`
        : '/jobs',
      title: nextCategories.length
        ? `Jobs Home : ${categorySummary}`
        : 'Jobs Home : All categories',
    });

    await fetchFilteredJobs(nextCategories, searchTerm.trim());
  };

  const HandleRemoveFilter = async (activeFilter: ActiveFilter) => {
    if (blockFilter) {
      return;
    }

    if (activeFilter.type === 'search') {
      await resetToInitialState();
      return;
    }

    if (activeFilter.type === 'category') {
      const nextCategories = selectedCategories.filter(
        (category) => category !== activeFilter.label
      );
      setSelectedCategories(nextCategories);
      selectedCategoriesRef.current = nextCategories;
      updateFilterUrl(nextCategories, searchTerm.trim());
      await fetchFilteredJobs(nextCategories, searchTerm.trim());
    }
  };

  const HandleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchTerm(value);

    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
    }

    const trimmedValue = value.trim();

    // Do not trigger search if the term is between 1 and 2 characters
    if (trimmedValue.length > 0 && trimmedValue.length < 3) {
      return;
    }

    searchDebounceRef.current = setTimeout(async () => {
      if (trimmedValue.length === 0) {
        ReactGA.send({
          hitType: 'pageview',
          page: '/jobs',
          title: 'Jobs Home : Search cleared',
        });
        await resetToInitialState();
        return;
      }

      const cleared: JobContent = {
        ...filterRef.current,
        category: '',
        subCategory: '',
      };
      setFilter(cleared);
      filterRef.current = cleared;
      updateFilterUrl(selectedCategoriesRef.current, trimmedValue);

      ReactGA.send({
        hitType: 'pageview',
        page: `/jobs?q=${trimmedValue}`,
        title: `Jobs Home : Search : ${trimmedValue}`,
      });

      await fetchFilteredJobs(selectedCategoriesRef.current, trimmedValue);
    }, SEARCH_DEBOUNCE_MS);
  };

  const HandleLoadMore = async () => {
    setBlockFilter(true);

    try {
      let newPage = page + 1;

      setPage(newPage);

      let addJobs = await GetJobs(
        selectedCategories,
        '',
        newPage,
        searchTerm.trim()
      );

      setJobs((prevJobs) => ({
        ...prevJobs,
        content: [...prevJobs.content, ...addJobs.content],
        last: addJobs.last,
      }));
    } catch (error: any) {
      if (error.message.includes('SHOW: ')) {
        Swal.fire({
          toast: true,
          position: 'top-end',
          icon: 'info',
          title: error.message.slice(5, error.message.length),
          showConfirmButton: false,
          timer: 3000,
          timerProgressBar: true,
          didOpen: (toast) => {
            toast.addEventListener('mouseenter', Swal.stopTimer);
            toast.addEventListener('mouseleave', Swal.resumeTimer);
          },
        });
      }
    }

    setBlockFilter(false);
  };

  const hashCodeConfirmation = async (hashCode: string | null) => {
    try {
      let response =
        await SubscriptionService.CheckSubscriptionHashCode(hashCode);
      if (response) {
        return true;
      }
    } catch (error: any) {
      searchParams.delete('subscription');
      searchParams.delete('message');
      searchParams.delete('hash');
      setModalOpen(false);

      navigate(
        { pathname: '/jobs', search: searchParams.toString() },
        { replace: true }
      );

      Swal.fire({
        toast: true,
        position: 'top',
        icon: 'info',
        title: `${error.message}`,
        showConfirmButton: false,
        timer: 3000,
        timerProgressBar: true,
        didOpen: (toast) => {
          toast.addEventListener('mouseenter', Swal.stopTimer);
          toast.addEventListener('mouseleave', Swal.resumeTimer);
        },
      });
      return false;
    }
  };

  const bgMap: Record<string, string> = {
    on: 'jo-modal-shell',
    error: 'jo-modal-shell--error',
    removed: 'jo-modal-shell--warning',
  };
  const bgClass = bgMap[subscriptionType ?? ''] ?? 'jo-modal-shell';

  const handleCloseModal = () => {
    setModalOpen(false);
    searchParams.delete('subscription');
    searchParams.delete('message');
    searchParams.delete('hash');
    navigate(
      { pathname: '/jobs', search: searchParams.toString() },
      { replace: true }
    );
  };

  const loadedJobCount = jobs.content.length;
  const totalJobCount = jobs.totalElements || loadedJobCount;
  const resultLabel = totalJobCount === 1 ? 'opportunity' : 'opportunities';
  const trimmedSearch = searchTerm.trim();
  const isSearchActive = trimmedSearch.length >= 3;

  const activeFilters: ActiveFilter[] = [];
  if (trimmedSearch.length >= 3) {
    activeFilters.push({
      id: 'search',
      label: `"${trimmedSearch}"`,
      type: 'search',
    });
  }
  if (selectedCategories.length > 0) {
    selectedCategories.forEach((category) => {
      activeFilters.push({
        id: `category-${category}`,
        label: category,
        type: 'category',
      });
    });
  }

  const categorySummary = formatCategorySummary(selectedCategories);

  const heroSubtitle = (() => {
    if (isSearchActive && selectedCategories.length > 0) {
      return `${totalJobCount} ${resultLabel} for "${trimmedSearch}" in ${categorySummary}`;
    }
    if (isSearchActive) {
      return `${totalJobCount} ${resultLabel} for "${trimmedSearch}" across all categories`;
    }
    if (selectedCategories.length > 0) {
      return `${totalJobCount} ${resultLabel} in ${categorySummary}`;
    }
    return 'Search by title or skill, then narrow by category.';
  })();

  return (
    <main className="public-page-shell w-full">
      {spinner && (
        <div className="spinner-container">
          <span className="spinner"></span>
        </div>
      )}

      <div className="public-content-rail ats-stack">
        <section className="ats-page-hero">
          <div className="flex flex-col gap-5 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <div className="public-eyebrow flex items-center gap-2">
                <SearchOutlined />
                Open roles
              </div>
              <h1
                className={`jo-heading m-0 text-2xl font-black sm:text-3xl lg:text-4xl${
                  isFiltering ? ' jobs-count-updating' : ''
                }`}
              >
                {totalJobCount} {resultLabel}
              </h1>
              <p className="jo-body-muted mb-0 mt-2 max-w-2xl break-words text-sm leading-6 sm:mt-3 sm:text-base sm:leading-7">
                {heroSubtitle}
              </p>
            </div>
            <Link
              to="/sign-up"
              className="public-button-secondary w-full px-4 sm:mt-1 sm:w-auto"
            >
              <UserAddOutlined />
              Post Jobs
            </Link>
          </div>

          <FilterPanel
            searchTerm={searchTerm}
            categories={categories}
            selectedCategories={selectedCategories}
            activeFilters={activeFilters}
            isFiltering={isFiltering}
            disabled={blockFilter || isFiltering}
            onSearchChange={HandleSearchChange}
            onCategorySelect={HandleCategorySelect}
            onRemoveFilter={HandleRemoveFilter}
            onClearAll={ClearFilter}
          />
        </section>

        <McpAvailabilityTeaser />

        <section
          className={`container-jobs grid grid-cols-1 gap-4 sm:grid-cols-2 sm:gap-5 lg:grid-cols-3 lg:gap-6${
            isFiltering ? ' jobs-filtering' : ''
          }${resultsEnter ? ' jobs-results-enter' : ''}`}
          aria-busy={isFiltering}
          aria-live="polite"
        >
          {isFiltering && (
            <div className="jobs-results-overlay" aria-hidden="true">
              <span className="jobs-results-spinner" />
            </div>
          )}

          {jobs.content.map((job, index) => (
            <div
              key={job.id || index}
              className="job-card h-full"
              style={
                resultsEnter
                  ? ({
                      animationDelay: `${Math.min(index, 11) * 40}ms`,
                    } as const)
                  : undefined
              }
            >
              <JobCard job={job} />
            </div>
          ))}

          {jobs.content.length === 0 && !spinner && !isFiltering && (
            <div className="public-panel col-span-full p-6 text-center sm:p-10">
              <div className="jo-empty-icon mb-4 text-5xl">
                <InboxOutlined />
              </div>
              <h3 className="jo-body-muted mb-2 text-2xl font-black">
                No jobs found
              </h3>
              <p className="jo-body-subtle mx-auto mb-0 max-w-md">
                Try a broader category, adjust your search, or check back later
                for new opportunities.
              </p>
            </div>
          )}

          {!jobs.last && jobs.content.length > 0 && (
            <div className="col-span-full flex justify-center px-1 pt-2 sm:px-0">
              <button
                className="public-button-primary w-full px-6 sm:w-auto"
                onClick={HandleLoadMore}
                disabled={blockFilter}
              >
                <ReloadOutlined />
                Load more
              </button>
            </div>
          )}
        </section>
      </div>

      <Modal
        open={modalOpen}
        onCancel={handleCloseModal}
        footer={null}
        centered
        width="100%"
        style={{ maxWidth: 600 }}
        styles={{ body: { padding: 0 } }}
      >
        <div
          className={`flex flex-col justify-center p-5 text-center sm:p-7 ${bgClass} rounded-lg`}
        >
          {modalBody}
        </div>
      </Modal>
    </main>
  );
};

export default Jobs;
